import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Breadcrumb, BreadcrumbElectionTypes, BreadcrumbGeographyTypes, BreadcrumbType, getGeographyUnitType, } from '../components/breadcrumbs/breadcrumb.model';
import { ElectionClient, OverviewClient } from "@shared/clients/api.generated";
import { EnumService } from "@valg-shared/services/enums/enum.service";
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from "@angular/common";

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {

  // Subject emitting the breadcrumb hierarchy
  private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

  // Observable exposing the breadcrumb hierarchy
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

  constructor(private readonly router: Router,
              private readonly electionClient: ElectionClient,
              private readonly overviewClient: OverviewClient,
              private readonly enumService: EnumService,
              private readonly translateService: TranslateService,
              private readonly datePipe: DatePipe,
              ) {
    this.router.events.pipe(
      // Filter the NavigationEnd events as the breadcrumb is updated only when the route reaches its end
      filter((event) => event instanceof NavigationEnd)
    ).subscribe(async () => {
      // Construct the breadcrumb hierarchy
      const root = this.router.routerState.snapshot.root;
      const breadcrumbs: Breadcrumb[] = [];
      await this.addBreadcrumb(root, [], breadcrumbs);
      // Emit the new hierarchy
      this._breadcrumbs$.next(breadcrumbs);
    });
  }

  private async addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
    if (route) {
      // Construct the route URL
      const currentUrl = route.url.map(url => url.path);
      const routeUrl = parentUrl.concat(currentUrl);

      if (route.data['breadcrumb']) {
        const label = this.getLabel(route, breadcrumbs, currentUrl[0]);
        const breadcrumb: Breadcrumb = {
          label: label,
          url: '/' + routeUrl.join('/'),
          type: route.data['type']
        };
        await this.enrichBreadcrumb(breadcrumb, currentUrl);
        breadcrumbs.push(breadcrumb);
      }
      // Add another element for the next route part
      if(route.firstChild){
        await this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
      }
    }
  }

  private async enrichBreadcrumb(breadcrumb: Breadcrumb, routeUrls: string[]) {
    if (BreadcrumbElectionTypes.includes(breadcrumb.type)) {
      await this.enrichElectionBreadcrumb(breadcrumb);
    } else if (BreadcrumbGeographyTypes.includes(breadcrumb.type)) {
      await this.enrichGeographyBreadcrumb(breadcrumb, routeUrls[1]);
    } else if (breadcrumb.type === BreadcrumbType.Page) {
      await this.enrichOtherBreadcrumb(breadcrumb);
    }
  }

  private async enrichElectionBreadcrumb(breadcrumb: Breadcrumb) {
    const election = await lastValueFrom(this.electionClient.getCurrentElection());

    if (election?.electionDate) {
      const electionDate = new Date(election.electionDate);
      const electionYear = electionDate.getFullYear();
      const electionType = this.enumService.translateValue(breadcrumb.label, 'ElectionType');
      breadcrumb.label = `${electionType} ${electionYear}`;
      breadcrumb.longLabel = `${electionType} ${this.datePipe.transform(electionDate, "longDate")}`;
    }
  }

  private async enrichGeographyBreadcrumb(breadcrumb: Breadcrumb, id: string) {
    const geographyType = getGeographyUnitType(breadcrumb.type);
    const geographyUnit = await lastValueFrom(this.overviewClient.getGeographyUnitName(geographyType, id));
    if (geographyUnit?.name) {
      breadcrumb.label = geographyUnit.name;
    }
  }

  private async enrichOtherBreadcrumb(breadcrumb: Breadcrumb) {
    breadcrumb.label = await this.translateService.get(breadcrumb.label).toPromise();
  }

  private getLabel(route: ActivatedRouteSnapshot, breadcrumbs: Breadcrumb[], url: string) {
    let label = "";
    if(route.data['type']){
      label = route.data['title'] ?? route.data['type'];
    }
    else{
      label = breadcrumbs.length > 0 ?  breadcrumbs[breadcrumbs.length - 1].label + '_' : "breadcrumb.pages.";
      label += (url ?? 'frontpage');
    }
    return label;
  }
}
